import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PoNotificationService, PoToasterOrientation } from '@po-ui/ng-components';
import { HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { EMPTY, Observable, lastValueFrom } from 'rxjs';
import { assert } from 'ats-ui-lib';
import { ResumeParserLinkedInService } from '../../root/resume-parser/resume-parser-linkedin.service';
import { FileSizePipe } from '../button-upload/file-size.pipe';
import { ResumeLinkedIn } from '../../root/resume-parser/resume';
import { ResumeUploadEventsService } from '../../root/resume-upload-events/resume-upload-events.service';
import {
  UploadEvents,
  UploadEventTypeEnum,
} from '../../root/resume-upload-events/resume-upload-events.model';
import { UploadFile } from '../../models/file.model';
import { UsageMetricsService } from '../../root/metrics/usage-metrics.service';
import { ResumeParserFailureReasonEnum, ResumeParserSourceEnum } from '../../models/metrics.enum';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'ats-button-upload-resume',
  templateUrl: './button-upload-resume.component.html',
  styleUrls: ['./button-upload-resume.component.css'],
  providers: [FileSizePipe],
  standalone: false,
})
export class ButtonUploadResumeComponent implements AfterViewInit, OnDestroy {
  @Input() label: string = 'l-continue';

  @Input() icon: string = '';

  @Input() isLoading: boolean = false;

  @Output() closeModal = new EventEmitter();

  @Output() parserError = new EventEmitter();

  @Output() sendDataApplicantToCheckInformation = new EventEmitter();

  @Input() acceptedExtensions: string = '';

  // 1 MB
  @Input() fileSizeLimit: number = 1024 * 1024;

  private selectFileWindowOnFocus?: any;

  private buttonUploadOnClick?: any;

  @ViewChild('fileUpload')
  private fileUploadElementRef!: ElementRef;

  private get fileUpload(): HTMLInputElement {
    return this.fileUploadElementRef.nativeElement;
  }

  constructor(
    private notificationService: PoNotificationService,
    private translateService: TranslateService,
    private resumeParserLinkedInService: ResumeParserLinkedInService,
    private fileSizePipe: FileSizePipe,
    private uploadEventsService: ResumeUploadEventsService,
    private window: Window,
    private metricsService: UsageMetricsService,
    private announcer: LiveAnnouncer,
  ) {}

  ngAfterViewInit(): void {
    this.addDialogClosedFileUpload(this.fileUpload!, this.onFileSelected.bind(this));
  }

  public openFileUpload(): void {
    this.fileUpload!.click();
  }

  public async onFileSelected(event?: any): Promise<void> {
    try {
      if (event === undefined) {
        this.closeModal.emit();
      } else {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          const file = target.files.item(0)!;
          const clonedFile = new File([file], file.name, { type: file.type });
          const { isValid, fileValidationFailureReason } = await this.fileValidate(clonedFile);
          if (isValid) {
            await this.upload(clonedFile);
          } else {
            assert(fileValidationFailureReason != null);
            this.metricsService.sendResumeParserMetric({
              isSuccess: false,
              parseSource: ResumeParserSourceEnum.LinkedIn,
              failureReason: fileValidationFailureReason,
            });
          }
        }
      }
    } finally {
      this.fileUpload.value = '';
    }
  }

  addDialogClosedFileUpload(
    input: HTMLInputElement,
    callback: Function,
    triggerFocus: boolean = false,
  ): void {
    assert(input);

    this.selectFileWindowOnFocus = (): void => {
      this.window.removeEventListener('focus', this.selectFileWindowOnFocus);
      callback();
    };

    this.buttonUploadOnClick = (): void => {
      this.window.addEventListener('focus', this.selectFileWindowOnFocus);

      if (triggerFocus) this.selectFileWindowOnFocus();
    };

    input.addEventListener('click', this.buttonUploadOnClick);
  }

  private async fileValidate(
    file: File,
  ): Promise<{ isValid: boolean; fileValidationFailureReason?: ResumeParserFailureReasonEnum }> {
    const translates = await lastValueFrom(
      this.translateService.get(['l-validate-file-extension', 'l-validate-file-size']),
    );

    if (!file.type.includes('pdf')) {
      this.parserError.emit();

      return {
        isValid: false,
        fileValidationFailureReason: ResumeParserFailureReasonEnum.InvalidExtension,
      };
    }

    if (file.size > this.fileSizeLimit) {
      const fileSizeFormatted = this.fileSizePipe.transform(this.fileSizeLimit);
      void this.announcer.announce(
        `${translates['l-validate-file-size']} ${fileSizeFormatted}`,
        'assertive',
      );

      this.notificationService.warning({
        message: `${translates['l-validate-file-size']} ${fileSizeFormatted}`,
        orientation: PoToasterOrientation.Top,
      });

      return {
        isValid: false,
        fileValidationFailureReason: ResumeParserFailureReasonEnum.FileSizeExceeded,
      };
    }

    this.closeModal.emit();

    return { isValid: true };
  }

  private async upload(file: File): Promise<void> {
    try {
      this.uploadEventsService.emitIsLoading(true);
      const resumeResponse = await this.resumeParserLinkedInService
        .parse(file, this.uploadEvents.bind(this), this.uploadError.bind(this))
        .toPromise();
      const resume = (resumeResponse as HttpResponse<ResumeLinkedIn>).body;
      const applicant = await this.resumeParserLinkedInService.transform(resume);

      if (resume) {
        const translation = this.translateService.instant([
          'l-parse-resume-upload-success-message',
        ]);
        this.sendDataApplicantToCheckInformation.emit(applicant);

        void this.announcer.announce(
          translation['l-parse-resume-upload-success-message'],
          'assertive',
        );

        this.notificationService.success({
          message: translation['l-parse-resume-upload-success-message'],
          orientation: PoToasterOrientation.Top,
        });
      }
    } finally {
      this.uploadEventsService.emitIsLoading(false);
    }
  }

  private uploadError(httpError: HttpErrorResponse): Observable<any> {
    if (httpError) {
      if (httpError.status === 400) {
        this.parserError.emit();
      } else {
        const translation = this.translateService.instant(['l-parse-resume-upload-fail-message']);

        void this.announcer.announce(
          translation['l-parse-resume-upload-success-message'],
          'assertive',
        );

        this.notificationService.error({
          message: translation['l-parse-resume-upload-fail-message'],
          orientation: PoToasterOrientation.Top,
        });
      }
      this.metricsService.sendResumeParserMetric({
        isSuccess: false,
        parseSource: ResumeParserSourceEnum.LinkedIn,
        failureReason:
          httpError.status === 400
            ? ResumeParserFailureReasonEnum.ParseError
            : ResumeParserFailureReasonEnum.UnknownError,
      });
    }

    this.uploadEventsService.reset(UploadEventTypeEnum.ResumeLinkedin);

    return EMPTY;
  }

  private uploadEvents(event: HttpEvent<any>, file: File): void {
    switch (event.type) {
      case HttpEventType.Sent:
        this.uploadEventsService.next(<UploadEvents>{
          progress: 0,
          isSending: true,
          type: UploadEventTypeEnum.ResumeLinkedin,
        });
        break;
      case HttpEventType.UploadProgress:
        this.uploadEventsService.next(<UploadEvents>{
          progress: event.total == null ? 100 : Math.round((event.loaded / event.total) * 100),
          isSending: true,
          type: UploadEventTypeEnum.ResumeLinkedin,
        });
        break;
      case HttpEventType.Response:
        this.uploadEventsService.next(<UploadEvents>{
          isSending: false,
          file: <UploadFile>{
            filename: file.name,
            size: file.size,
            type: file.type,
          },
          type: UploadEventTypeEnum.ResumeLinkedin,
        });
        if (event.status === 200) {
          this.metricsService.sendResumeParserMetric({
            isSuccess: true,
            parseSource: ResumeParserSourceEnum.LinkedIn,
          });
        }
        break;
      default:
    }
  }

  ngOnDestroy(): void {
    this.fileUpload!.removeEventListener('click', this.buttonUploadOnClick);
    this.window.removeEventListener('focus', this.selectFileWindowOnFocus);
  }
}
